import {Transition} from '@mantine/core';
import {useEffect, useState} from 'react';
import {GoogleAdBannerName} from '../../../../config/constants/googleAdBanner';
import {
  displayStartTime,
  maintenanceEndTime,
} from '../../../../config/constants/maintenance';
import {MAIL_AUTH} from '../../../../config/constants/routes';
import type {ReportsResponseType} from '../../../../core/repositories/reportsRepository';
import {GoogleAdBanner} from '../../../../shared/components/Banner/GoogleAdBanner';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {SignUpPromptModal} from '../../../../shared/components/SignUpPromptModal';
import {useGoogleAdBanner} from '../../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../../shared/services/useMediaQuery';
import {GeneralCarousel} from '../../../generalCarousel/components/GeneralCarousel';
import {Announcement} from '../../../maintenance/components/Annoucement';
import {MaintenanceTop} from '../../../maintenance/components/MaintenanceTop';
import {FindColumnByKeyword} from './FindColumnByKeyword';
import {LogInGuideHeaderForPc} from './LogInGuideHeaderForPc';
import {LogInGuideHeaderForSp} from './LogInGuideHeaderForSp';
import {LoggedInTopRecommend} from './LoggedInTopRecommend';
import {NoLoggedInCommunityList} from './NoLoggedInCommunityList';
import {NoLoggedInReportIndustryList} from './NoLoggedInReportIndustryList';
import {NoLoggedInReportList} from './NoLoggedInReportList';
import {CompanyListForNoLoggedIn} from './Presenter/CompanyListForNoLoggedIn';
import {NoLoggedInColumnList} from './Presenter/NoLoggedInColumnList';
import {NoLoggedInEventGuide} from './Presenter/NoLoggedInEventGuide';
import {RecruitmentListForNoLoggedIn} from './Presenter/RecruitmentListForNoLoggedIn';

type StaticCompany = {
  id: number;
  companyName: string;
};

type TopCompany = {
  id: number;
  name: string;
  logo_url: string;
  background_image_url: string;
  catch_phrase: string;
};

type NoLoggedInTopProps = {
  reportsData?: ReportsResponseType;
  staticCompanies?: StaticCompany[];
  topCompanies?: {
    companies: TopCompany[];
  };
  isReleasedRedesignTop: boolean;
};

/**
 * 未ログイントップページコンテンツ
 */
export const NoLoggedInTop: React.FC<NoLoggedInTopProps> = ({
  reportsData,
  staticCompanies,
  topCompanies,
  isReleasedRedesignTop,
}) => {
  const {isPC} = useMediaQuery();
  const googleAdBannerNames = [GoogleAdBannerName.RICH_DESKTOP_HEADER];
  useGoogleAdBanner(googleAdBannerNames);
  const maintenanceNow = new Date();
  const isMaintenance =
    maintenanceEndTime > maintenanceNow && maintenanceNow > displayStartTime;

  // アナウンスを表示する or しない
  const isAnnouncement = false;

  // モーダル状態管理
  const [isModalOpen, setIsModalOpen] = useState(false);

  // スクロール状態管理
  const [showFixedFooter, setShowFixedFooter] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // スクロール位置が100px以上で表示
      setShowFixedFooter(window.scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (isReleasedRedesignTop) {
    return (
      <div className="w-full pc:max-w-[1200px] px-16 pc:px-0">
        {isAnnouncement && <Announcement />}
        <div className="hidden pc:block">
          {isMaintenance && <MaintenanceTop />}
        </div>
        <div className="w-[1200px] mx-auto bg-background-1 hidden pc:block">
          <GoogleAdBanner
            googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
          />
        </div>
        <div className="pc:hidden">
          <GeneralCarousel
            componentId="toppage-general-carousel-top"
            startIndex={0}
            endIndex={5}
          />
        </div>
        <div>
          <div className="mb-16">
            <LogInGuideHeaderForPc />
            <LogInGuideHeaderForSp />
          </div>
          <div>
            <LoggedInTopRecommend isLogin={false} />
          </div>
        </div>
        <SignUpPromptModal
          opened={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
        <Transition mounted={showFixedFooter} transition="fade" duration={500}>
          {(styles) => (
            <div
              style={styles}
              className="pc:hidden fixed bottom-0 flex w-full bg-overlay-3 p-16 justify-center z-40"
            >
              <CommonLink
                href={MAIL_AUTH}
                className="bg-accent-1 text-white font-bold p-16 rounded-4"
              >
                無料会員登録はこちら
              </CommonLink>
            </div>
          )}
        </Transition>
      </div>
    );
  }

  return (
    <div className="w-full pc:w-[1200px]">
      {isAnnouncement && <Announcement />}
      {isPC && (
        <>
          {isMaintenance && <MaintenanceTop />}
          <div className="w-[1200px] mx-auto  bg-background-1">
            <GoogleAdBanner
              googleAdBannerName={GoogleAdBannerName.RICH_DESKTOP_HEADER}
            />
          </div>
        </>
      )}

      <LogInGuideHeaderForPc />
      <LogInGuideHeaderForSp />
      <NoLoggedInColumnList />
      {isPC && (
        <CompanyListForNoLoggedIn
          staticCompanies={staticCompanies ?? []}
          topCompanies={topCompanies}
        />
      )}
      {isPC && <NoLoggedInEventGuide />}
      {isPC && (
        <div className="w-[1200px] mt-16 flex justify-between">
          <NoLoggedInCommunityList />
          <NoLoggedInReportIndustryList />
        </div>
      )}
      {!isPC && <NoLoggedInReportList reportsData={reportsData} />}
      {!isPC && (
        <CompanyListForNoLoggedIn
          staticCompanies={staticCompanies ?? []}
          topCompanies={topCompanies}
        />
      )}
      <RecruitmentListForNoLoggedIn />
      <FindColumnByKeyword />
    </div>
  );
};
